// @TODO: turn this into a more generic component that has the same Props API as MUI https://mui.com/material-ui/react-card/
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import Link from "next/link";
import type { ReactNode } from "react";
import React from "react";

import classNames from "@calcom/ui/classNames";

import { Button } from "../button";

const cvaCardTypeByVariant = cva("", {
  // Variants won't have any style by default. Style will only be applied if the variants are combined.
  // So, style is defined in compoundVariants.
  variants: {
    variant: {
      basic: "",
      ProfileCard: "",
      SidebarCard: "",
      NewLaunchSidebarCard: "",
    },
    structure: {
      image: "",
      card: "",
      title: "",
      description: "",
    },
  },
  compoundVariants: [
    // Style for Basic Variants types
    {
      variant: "basic",
      structure: "image",
      className: "w-10 h-auto",
    },
    {
      variant: "basic",
      structure: "card",
      className: "p-5",
    },
    {
      variant: "basic",
      structure: "title",
      className: "text-base mt-4",
    },
    {
      variant: "basic",
      structure: "description",
      className: "text-sm leading-[18px] text-white font-normal",
    },

    // Style for ProfileCard Variant Types
    {
      variant: "ProfileCard",
      structure: "image",
      className: "w-9 h-auto rounded-full mb-4s",
    },
    {
      variant: "ProfileCard",
      structure: "card",
      className: "w-80 p-4 hover:bg-subtle",
    },
    {
      variant: "ProfileCard",
      structure: "title",
      className: "text-base",
    },
    {
      variant: "ProfileCard",
      structure: "description",
      className: "text-sm leading-[18px] text-white font-normal",
    },

    // Style for SidebarCard Variant Types
    {
      variant: "SidebarCard",
      structure: "image",
      className: "w-9 h-auto rounded-full mb-4s",
    },
    {
      variant: "SidebarCard",
      structure: "card",
      className: "w-full p-3 border border-subtle",
    },
    {
      variant: "SidebarCard",
      structure: "title",
      className: "text-sm font-cal",
    },
    {
      variant: "SidebarCard",
      structure: "description",
      className: "text-xs text-default line-clamp-2",
    },

    // Style for NewLaunchSidebarCard Variant Types
    {
      variant: "NewLaunchSidebarCard",
      structure: "image",
      className: "w-9 h-auto rounded-full mb-4s",
    },
    {
      variant: "NewLaunchSidebarCard",
      structure: "card",
      className: "w-full p-3 border border-subtle bg-launch-dark text-white",
    },
    {
      variant: "NewLaunchSidebarCard",
      structure: "title",
      className: "text-sm font-cal text-white",
    },
    {
      variant: "NewLaunchSidebarCard",
      structure: "description",
      className: "text-xs text-white",
    },
  ],
});

type CVACardType = Required<Pick<VariantProps<typeof cvaCardTypeByVariant>, "variant">>;

export interface BaseCardProps extends CVACardType {
  image?: string;
  icon?: ReactNode;
  imageProps?: JSX.IntrinsicElements["img"];
  title: string;
  description: ReactNode;
  containerProps?: JSX.IntrinsicElements["div"];
  actionButton?: {
    href?: string;
    child: ReactNode;
    onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    "data-testid"?: string;
  };
  learnMore?: {
    href: string;
    text: string;
  };
  mediaLink?: string;
  thumbnailUrl?: string;
  structure?: string;
  coverPhoto?: string;
  buttonClassName?: string;
}

export function Card({
  image,
  title,
  icon,
  description,
  variant,
  actionButton,
  containerProps,
  imageProps,
  mediaLink,
  thumbnailUrl,
  learnMore,
}: BaseCardProps) {
  const LinkComponent = learnMore && learnMore.href.startsWith("https") ? "a" : Link;
  return (
    <div
      className={classNames(
        containerProps?.className,
        cvaCardTypeByVariant({ variant, structure: "card" }),
        "border-subtle flex flex-col justify-between rounded-md border text-white"
      )}
      style={{
        background:
          "linear-gradient(136deg, var(--theme-primary-tints-t4, #806EEE) 0%, var(--theme-primary-shades-s3, #060B3C) 100%)",
      }}
      data-testid="card-container"
      {...containerProps}>
      <div>
        {icon && icon}
        {image && (
          <img
            src={image}
            // Stops eslint complaining - not smart enough to realise it comes from ...imageProps
            alt={imageProps?.alt}
            className={classNames(
              imageProps?.className,
              cvaCardTypeByVariant({ variant, structure: "image" })
            )}
            {...imageProps}
          />
        )}
        <h5
          title={title}
          className={classNames(
            cvaCardTypeByVariant({ variant, structure: "title" }),
            "line-clamp-1 font-bold leading-5 text-white"
          )}>
          {title}
        </h5>
        {description && (
          <p
            title={description.toString()}
            className={classNames(cvaCardTypeByVariant({ variant, structure: "description" }), "pt-1")}>
            {description}
          </p>
        )}
      </div>
      {variant === "SidebarCard" && mediaLink && (
        <a
          onClick={actionButton?.onClick}
          target="_blank"
          rel="noreferrer"
          href={mediaLink}
          data-testid={actionButton?.["data-testid"]}
          className="group relative my-3 flex aspect-video items-center overflow-hidden rounded">
          <div className="absolute inset-0  transition group-hover:bg-opacity-40" />
          <svg
            className="text-inverted absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 transform rounded-full shadow-lg transition-all hover:-mt-px"
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <rect
              x="0.965332"
              y="0.979492"
              width="38.8121"
              height="38.8121"
              rx="19.406"
              fill="url(#paint0_linear_385_16106)"
            />
            <rect
              x="1.73798"
              y="1.75214"
              width="37.2668"
              height="37.2668"
              rx="18.6334"
              stroke="#3116C8"
              strokeOpacity="0.2"
              strokeWidth="1.5453"
            />
            <path
              d="M14.9629 12.2729L27.5828 20.3858L14.9629 28.4986V12.2729Z"
              stroke="#F5F4FC"
              strokeWidth="2.0282"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_385_16106"
                x1="0.965332"
                y1="0.979492"
                x2="14.2562"
                y2="46.9105"
                gradientUnits="userSpaceOnUse">
                <stop stopColor="#806EEE" />
                <stop offset="1" stopColor="#3116C8" />
              </linearGradient>
            </defs>
          </svg>

          <img alt="play feature video" src={thumbnailUrl} />
        </a>
      )}

      {/* TODO: this should be CardActions https://mui.com/material-ui/api/card-actions/ */}
      {variant === "basic" && actionButton && (
        <div>
          <Button
            color="secondary"
            href={actionButton?.href}
            className="mt-10"
            EndIcon="arrow-right"
            data-testid={actionButton["data-testid"]}>
            {actionButton?.child}
          </Button>
        </div>
      )}

      {(variant === "SidebarCard" || variant === "NewLaunchSidebarCard") && (
        <div className="mt-2 flex items-center justify-center">
          {learnMore && (
            <LinkComponent
              href={learnMore.href}
              onClick={actionButton?.onClick}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-white">
              {learnMore.text}
            </LinkComponent>
          )}
          {actionButton?.child && (
            <button
              className="p-0 text-xs font-normal text-white hover:text-white"
              color="minimal"
              data-testid={actionButton?.["data-testid"]}
              onClick={actionButton?.onClick}>
              {actionButton?.child}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Card;
